import React from 'react';
import { formatDate } from "@/utils/dateUtils";
import { format, Locale } from "date-fns";
import { cn } from "@/lib/utils";
import { WeeklyTimeSlot } from "./WeeklyTimeSlot";
import { useTheme } from "@/components/theme/ThemeProvider";
import { scheduleHeaderHeigth, scheduleHeaderHeigthMobile, scheduleSlotsHeigth, getScheduleSlotsHeightMobile, ProcessedSlot, getWeeklySlotHeight, getWeeklyHeaderHeight } from '@/utils/scheduleUtils';

const GAP_HEIGHT = 30; // Hauteur fixe pour les indicateurs de gap

interface WeeklyDayColumnProps {
  date: Date;
  index: number;
  processedSlots: ProcessedSlot[];
  slotDuration: number;
  startTime: string;
  selectedDate: Date;
  getClassForTimeSlot: (date: Date, timeSlot: string) => any;
  getClassesForTimeSlot?: (date: Date, timeSlot: string) => any[];
  isAdmin: boolean;
  onDateSelect: (date: Date) => void;
  onAddClass: (date: Date, time: string) => void;
  onHandleClassClick: (classInfo: any, date: Date) => void;
  locale: Locale;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  isMobile?: boolean;
  recentlyBookedSlots?: Set<string>;
  realtimeModifiedSlots?: Set<string>;
  bookingAdvanceDays?: number;
  compact?: boolean;
}

export const WeeklyDayColumn: React.FC<WeeklyDayColumnProps> = ({
  date,
  index,
  processedSlots,
  slotDuration,
  startTime,
  selectedDate,
  getClassForTimeSlot,
  getClassesForTimeSlot,
  isAdmin,
  onDateSelect,
  onAddClass,
  onHandleClassClick,
  locale,
  isClassBooked = () => false,
  isMobile,
  recentlyBookedSlots = new Set(),
  realtimeModifiedSlots = new Set(),
  bookingAdvanceDays = 0,
  compact = false
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Day header
  const dayName = format(date, isMobile ? 'EEEEE' : 'EEE', { locale });
  const dayNumber = format(date, 'd', { locale });
  const todayKey = formatDate(new Date(), 'yyyy-MM-dd');
  const thisKey = formatDate(date, 'yyyy-MM-dd');
  const isToday = thisKey === todayKey;
  const isSelected = thisKey === formatDate(selectedDate, 'yyyy-MM-dd');

  // Extraire les timeSlots des processedSlots pour calculer les positions
  const timeSlots = processedSlots
    .filter((s): s is { type: 'slot'; time: string } => s.type === 'slot')
    .map(s => s.time);

  // Helper pour calculer le temps d'un slot à partir d'un temps de départ
  const calculateSlotTime = (baseTime: string, slotOffset: number, duration: number): string => {
    const [hours, minutes] = baseTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + (slotOffset * duration);
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  };

  const occupiedSlots = new Map<string, { isStart: boolean; classes: any[]; maxDurationSlots: number }>();
  timeSlots.forEach(timeSlot => {
    const classesAtSlot = getClassesForTimeSlot
      ? getClassesForTimeSlot(date, timeSlot).filter(c => c.time === timeSlot)
      : (() => { const c = getClassForTimeSlot(date, timeSlot); return c && c.time === timeSlot ? [c] : []; })();

    if (classesAtSlot.length > 0) {
      let maxSlotsNeeded = 1;

      for (const classInfo of classesAtSlot) {
        const durationMinutes = parseInt(classInfo.duration) || slotDuration;
        const slotsNeeded = Math.ceil(durationMinutes / slotDuration);
        maxSlotsNeeded = Math.max(maxSlotsNeeded, slotsNeeded);

        for (let i = 1; i < slotsNeeded; i++) {
          const coveredSlotTime = calculateSlotTime(timeSlot, i, slotDuration);
          if (timeSlots.includes(coveredSlotTime) && !occupiedSlots.has(coveredSlotTime)) {
            occupiedSlots.set(coveredSlotTime, { isStart: false, classes: [], maxDurationSlots: 1 });
          }
        }
      }

      occupiedSlots.set(timeSlot, { isStart: true, classes: classesAtSlot, maxDurationSlots: maxSlotsNeeded });
    }
  });

  return (
    <div
      className={cn(
        "flex flex-col border-l",
        isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
      )}
    >
      {/* Column Header */}
      <button
        onClick={() => onDateSelect(date)}
        data-selected={isSelected || isToday ? "true" : undefined}
        className={cn(
          "week-day-header text-center border-b",
          isDarkMode ? "border-gray-700" : "border-gray-200",
          isToday
            ? isDarkMode
              ? "bg-blue-900 text-white"
              : "bg-blue-50 text-blue-700"
            : isDarkMode
            ? "bg-gray-800 text-gray-200"
            : "bg-gray-50 text-gray-800",
          isSelected && (isDarkMode
            ? "ring-2 ring-blue-400 ring-inset"
            : "ring-2 ring-blue-500 ring-inset")
        )}
        style={{ height: getWeeklyHeaderHeight(!!isMobile, compact) }}
      >
        <div className={cn(
          "font-medium uppercase",
          isMobile ? "text-[8px]" : "text-sm"
        )}>
          {isMobile ? dayName.substring(0, 1) : dayName}
        </div>
        <div className={cn(
          "mt-0.5 font-semibold rounded-full mx-auto flex items-center justify-center",
          isToday
            ? isDarkMode
              ? "bg-blue-600 text-white"
              : "bg-blue-500 text-white"
            : isDarkMode
            ? "bg-gray-700 text-gray-200"
            : "",
          isMobile ? "h-4 w-4 text-[8px]" : "h-8 w-8 text-lg"
        )}>
          {dayNumber}
        </div>
      </button>

      {/* Time Slots and Gaps */}
      <div className="flex-1 flex flex-col">
        {processedSlots.map((slot, idx) => {
          if (slot.type === 'gap') {
            // Rendu du gap grisé vide
            return (
              <div 
                key={`gap-${slot.startTime}-${idx}`}
                className={cn(
                  "border-b",
                  isDarkMode 
                    ? "bg-muted/10 border-muted/30" 
                    : "bg-gray-50/80 border-gray-200/50"
                )}
                style={{ height: GAP_HEIGHT }}
              />
            );
          }

          const timeSlot = slot.time;
          const slotInfo = occupiedSlots.get(timeSlot);
          const isOccupied = Boolean(slotInfo);

          if (isOccupied && !slotInfo.isStart) {
            return null;
          }

          const overlappingClasses = isOccupied ? slotInfo.classes : [];
          const slotKeys = overlappingClasses.map((c: any) => `${c.id}-${thisKey}-${timeSlot}`);
          const isRecentlyBooked = slotKeys.some((k: string) => recentlyBookedSlots.has(k));
          const isRealtimeModified = slotKeys.some((k: string) => realtimeModifiedSlots.has(k));
          
          return (
            <WeeklyTimeSlot
              key={`${thisKey}-${timeSlot}-${idx}`}
              date={date}
              time={timeSlot}
              classInfo={overlappingClasses.length === 1 ? overlappingClasses[0] : null}
              overlappingClasses={overlappingClasses}
              isAdmin={isAdmin}
              onAddClass={onAddClass}
              onHandleClassClick={onHandleClassClick}
              isClassBooked={isClassBooked}
              isFirstSlotOfClass={Boolean(isOccupied && slotInfo.isStart)}
              timeSlotHeight={getWeeklySlotHeight(!!isMobile, compact, slotDuration)}
              classDurationInSlots={isOccupied ? slotInfo.maxDurationSlots : 1}
              isMobile={isMobile}
              isRecentlyBooked={isRecentlyBooked}
              isRealtimeModified={isRealtimeModified}
              bookingAdvanceDays={bookingAdvanceDays}
              compact={compact}
            />
          );
        })}
      </div>
    </div>
  );
};
